// Enhanced Logger utility for environment-specific logging with structured logging
const LOG_LEVEL = process.env.REACT_APP_LOG_LEVEL || 'debug';
const ENABLE_API_LOGGING = process.env.REACT_APP_ENABLE_API_LOGGING === 'true' || process.env.REACT_APP_ENVIRONMENT === 'dev';
const LOG_API_REQUESTS = process.env.REACT_APP_LOG_API_REQUESTS === 'true' || process.env.REACT_APP_ENVIRONMENT === 'dev';
const LOG_API_RESPONSES = process.env.REACT_APP_LOG_API_RESPONSES === 'true' || process.env.REACT_APP_ENVIRONMENT === 'dev';

export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error'
}

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  data?: any;
  component?: string;
  userId?: string;
  sessionId?: string;
}

class Logger {
  private logLevel: LogLevel;
  private sessionId: string;

  constructor() {
    this.logLevel = LOG_LEVEL as LogLevel;
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  private shouldLog(level: LogLevel): boolean {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const currentLevelIndex = levels.indexOf(this.logLevel);
    const messageLevelIndex = levels.indexOf(level);
    
    return messageLevelIndex >= currentLevelIndex;
  }

  private createLogEntry(level: LogLevel, message: string, data?: any, component?: string): LogEntry {
    return {
      timestamp: new Date().toISOString(),
      level: level.toUpperCase(),
      message,
      data,
      component,
      sessionId: this.sessionId
    };
  }

  private log(level: LogLevel, message: string, data?: any, component?: string): void {
    if (this.shouldLog(level)) {
      const logEntry = this.createLogEntry(level, message, data, component);
      
      if (data) {
        console.log(`[${logEntry.timestamp}] [${logEntry.level}] [${component || 'APP'}] ${message}`, logEntry);
      } else {
        console.log(`[${logEntry.timestamp}] [${logEntry.level}] [${component || 'APP'}] ${message}`);
      }
    }
  }

  debug(message: string, data?: any, component?: string): void {
    this.log(LogLevel.DEBUG, message, data, component);
  }

  info(message: string, data?: any, component?: string): void {
    this.log(LogLevel.INFO, message, data, component);
  }

  warn(message: string, data?: any, component?: string): void {
    this.log(LogLevel.WARN, message, data, component);
  }

  error(message: string, data?: any, component?: string): void {
    this.log(LogLevel.ERROR, message, data, component);
  }

  // API-specific logging methods
  logApiRequest(method: string, url: string, data?: any, headers?: any): void {
    if (ENABLE_API_LOGGING && LOG_API_REQUESTS) {
      this.info(`API Request: ${method} ${url}`, {
        method,
        url,
        data,
        headers: this.sanitizeHeaders(headers)
      }, 'API');
    }
  }

  logApiResponse(method: string, url: string, status: number, data?: any, responseTime?: number): void {
    if (ENABLE_API_LOGGING && LOG_API_RESPONSES) {
      const level = status >= 400 ? LogLevel.ERROR : LogLevel.INFO;
      this.log(level, `API Response: ${method} ${url} - ${status}`, {
        method,
        url,
        status,
        data,
        responseTime: responseTime ? `${responseTime}ms` : undefined
      }, 'API');
    }
  }

  logApiError(method: string, url: string, error: any): void {
    if (ENABLE_API_LOGGING) {
      this.error(`API Error: ${method} ${url}`, {
        method,
        url,
        error: error.message || error,
        stack: error.stack
      }, 'API');
    }
  }

  private sanitizeHeaders(headers: any): any {
    if (!headers) return headers;
    
    const sanitized = { ...headers };
    // Remove sensitive headers
    delete sanitized.Authorization;
    delete sanitized['X-API-Key'];
    delete sanitized.Cookie;
    
    return sanitized;
  }

  // User action logging
  logUserAction(action: string, data?: any): void {
    this.info(`User Action: ${action}`, data, 'USER');
  }

  // Performance logging
  logPerformance(operation: string, duration: number, data?: any): void {
    this.info(`Performance: ${operation} took ${duration}ms`, data, 'PERF');
  }
}

export const logger = new Logger();
